/** Demo Mode only - Finance > Overview tab. Headline metric tiles, revenue/GP/EBIT trend
 * (actual-to-date transitioning to forecast), EBIT bridge preview, top/bottom contract
 * contributors, contracts requiring attention, and finance exception cards. Also carries
 * forward the "site finance operations" tiles from the old FinanceDashboard page (invoice/
 * credit/delivery-reconciliation exceptions), so that functionality isn't orphaned. */

import { useEffect, useMemo } from 'react';
import { Box, Button, Grid, SvgIcon, Typography, useTheme } from '@mui/material';
import {
  AttachMoney, TrendingUp, TrendingDown, Restaurant, People, Assessment, ReceiptLong, RequestQuote, LocalShipping, AccountBalanceWallet,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import {
  companyMonthlyTrend, computeTotals, sumLineItems, classifyFinancialStatus,
} from '../../../mocks/data/financeData';
import { DELIVERY_RECONCILIATION_ROWS } from '../../../mocks/data/deliveryReconciliationData';
import { useFinanceFilters } from '../FinanceFiltersContext';
import MetricTile, { formatSignedPoints } from '../components/MetricTile';
import TrendChart from '../components/TrendChart';
import FinanceExceptionCards from '../components/FinanceExceptionCards';
import { evaluateFinanceExceptions } from '../financeExceptions';

const OverviewTab = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { scopedContracts } = useFinanceFilters();

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value: number) => (value < 0 ? `(${applyRoundAndFormat(Math.abs(value), currencyCode)})` : applyRoundAndFormat(value, currencyCode));

  const budget = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.budget)));
  const forecast = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.currentForecast)));

  const tiles = [
    {
      key: 'revenue', label: t('financeOverviewRevenue', 'Revenue'), value: formatCurrency(forecast.totalRevenue), subtitle: `${t('financeOverviewVsBudget', 'vs budget')} ${formatCurrency(budget.totalRevenue)}`, icon: <AttachMoney />, color: '#1976d2',
    },
    {
      key: 'grossProfit', label: t('financeOverviewGrossProfit', 'Gross profit'), value: formatCurrency(forecast.grossProfit), subtitle: `${forecast.grossProfitPercentage.toFixed(1)}% GP`, icon: <Restaurant />, color: '#2e7d32',
    },
    {
      key: 'labourCost', label: t('financeOverviewLabourCost', 'Labour cost'), value: formatCurrency(forecast.totalLabour), subtitle: `${forecast.labourPercentage.toFixed(1)}% of revenue`, icon: <People />, color: '#ed6c02',
    },
    {
      key: 'ebit', label: t('financeOverviewEbit', 'EBIT'), value: formatCurrency(forecast.ebit), subtitle: `${t('financeOverviewMargin', 'Margin')} ${forecast.ebitMarginPercentage.toFixed(1)}%`, icon: forecast.ebit >= budget.ebit ? <TrendingUp /> : <TrendingDown />, color: forecast.ebit >= budget.ebit ? '#2e7d32' : '#d32f2f',
    },
    {
      key: 'forecastVariance', label: t('financeOverviewForecastVariance', 'Forecast variance to budget'), value: `${formatSignedPoints(forecast.ebitMarginPercentage - budget.ebitMarginPercentage)}`, subtitle: `${formatCurrency(forecast.ebit - budget.ebit)} EBIT`, icon: <Assessment />, color: '#9c27b0',
    },
  ];

  const trendCompany = companyMonthlyTrend('currentForecast');
  const trendBudget = companyMonthlyTrend('budget');
  const trendPriorYear = companyMonthlyTrend('priorYear');

  const contributors = useMemo(() => scopedContracts
    .map((c) => ({ contract: c, ebit: computeTotals(c.scenarios.currentForecast).ebit }))
    .sort((a, b) => b.ebit - a.ebit), [scopedContracts]);

  const attention = useMemo(() => scopedContracts.filter((c) => {
    const status = classifyFinancialStatus(c);
    return status === 'At risk' || status === 'Materially behind plan';
  }), [scopedContracts]);

  const exceptions = useMemo(() => evaluateFinanceExceptions(scopedContracts, formatCurrency).slice(0, 6),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scopedContracts, currencyCode]);

  const operationalFinanceTiles = useMemo(() => {
    const invoiceExceptions = DELIVERY_RECONCILIATION_ROWS.filter((row) => row.status !== 'Matched').length;
    const creditsOutstanding = DELIVERY_RECONCILIATION_ROWS.filter((row) => row.credit && row.credit.includes('pending')).length;
    const goodsReceiptMismatches = DELIVERY_RECONCILIATION_ROWS.filter((row) => row.goodsReceipt === '-' || row.receivedQuantity !== row.invoicedQuantity).length;
    const disputes = DELIVERY_RECONCILIATION_ROWS.filter((row) => row.status === 'Disputed').length;
    return [
      {
        key: 'invoiceExceptions', label: t('financeDashboardInvoiceExceptions', 'Invoice exceptions'), value: String(invoiceExceptions), subtitle: t('financeDashboardInvoiceExceptionsSubtitle', 'Awaiting match or approval'), icon: <ReceiptLong />, color: '#ed6c02', href: '/invoices',
      },
      {
        key: 'creditsOutstanding', label: t('financeDashboardCreditsOutstanding', 'Credits outstanding'), value: String(creditsOutstanding), subtitle: t('financeDashboardCreditsOutstandingSubtitle', 'Pending supplier confirmation'), icon: <RequestQuote />, color: '#9c27b0', href: '/credits',
      },
      {
        key: 'goodsReceiptMismatches', label: t('financeDashboardGoodsReceiptMismatches', 'Goods receipt mismatches'), value: String(goodsReceiptMismatches), subtitle: t('financeDashboardGoodsReceiptMismatchesSubtitle', 'Received vs invoiced quantity'), icon: <LocalShipping />, color: '#0288d1', href: '/delivery-reconciliation',
      },
      {
        key: 'supplierDisputes', label: t('financeDashboardSupplierDisputes', 'Supplier invoice disputes'), value: String(disputes), subtitle: t('financeDashboardSupplierDisputesSubtitle', 'Open commercial risk'), icon: <AccountBalanceWallet />, color: '#d32f2f', href: '/delivery-reconciliation',
      },
    ];
  }, [t]);

  return (
    <Box>
      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {tiles.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={2.4}
            key={tile.key}>
            <MetricTile {...tile} />
          </Grid>
        ))}
      </Grid>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        <Grid item
          xs={12}
          md={8}>
          <Section style={{ p: 4 }}>
            <TrendChart
              title={t('financeOverviewTrendTitle', 'Revenue trend - actual vs budget vs prior year')}
              series={[
                { label: t('financeOverviewCurrentForecast', 'Actual + current forecast'), data: trendCompany.revenue, color: '#1976d2' },
                { label: t('financePnlBudget', 'Budget'), data: trendBudget.revenue, color: theme.palette.custom.gray[400], dashed: true },
                { label: t('financePnlPriorYear', 'Prior year'), data: trendPriorYear.revenue, color: '#9c27b0' },
              ]}
              valueFormatter={(v) => (v === null ? '' : applyRoundAndFormat(v, currencyCode))}
            />
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={4}>
          <Section style={{ p: 4, height: '100%' }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 2 }}>
              {t('financeOverviewContributors', 'Top contract contributors (EBIT)')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {contributors.slice(0, 3).map(({ contract, ebit }) => (
                <Box key={contract.id}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="textSM">{contract.client}</Typography>
                  <Typography variant="textSM"
                    fontWeight={600}
                    color="success.main">{formatCurrency(ebit)}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mt: 3, mb: 2 }}>
              {t('financeOverviewNegativeContributors', 'Lowest contract contributors (EBIT)')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {contributors.slice(-3).reverse().map(({ contract, ebit }) => (
                <Box key={contract.id}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="textSM">{contract.client}</Typography>
                  <Typography variant="textSM"
                    fontWeight={600}
                    color={ebit < 0 ? 'error.main' : 'text.primary'}>{formatCurrency(ebit)}</Typography>
                </Box>
              ))}
            </Box>
          </Section>
        </Grid>
      </Grid>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4 }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 2 }}>
              {t('financeOverviewAttention', 'Contracts requiring attention')}
            </Typography>
            {attention.length === 0 && (
              <Typography variant="textSM"
                color="text.secondary">{t('financeOverviewNoAttention', 'No contracts are currently at risk or materially behind plan.')}</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {attention.map((contract) => (
                <Box key={contract.id}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => navigate(`/finance-performance/contracts/${contract.id}`)}>
                  <Box>
                    <Typography variant="textSM"
                      fontWeight={500}>{contract.client}</Typography>
                    <Typography variant="textXS"
                      color="text.secondary">{contract.situation}</Typography>
                  </Box>
                  <Typography variant="textSM"
                    fontWeight={600}
                    color={classifyFinancialStatus(contract) === 'Materially behind plan' ? 'error.main' : 'warning.main'}>
                    {classifyFinancialStatus(contract)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ p: 4 }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 2 }}>
              {t('financeOverviewExceptions', 'Finance exceptions')}
            </Typography>
            <FinanceExceptionCards exceptions={exceptions}
              emptyMessage={t('financeOverviewNoExceptions', 'No finance exceptions in scope.')} />
          </Section>
        </Grid>
      </Grid>

      <Typography variant="textMD"
        fontWeight={600}
        sx={{ mb: 3 }}>
        {t('financeDashboardSiteFinanceOperations', 'Site finance operations')}
      </Typography>
      <Grid container
        spacing={4}>
        {operationalFinanceTiles.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.key}>
            <Section style={{ p: 4, mb: 0, cursor: 'pointer' }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => navigate(tile.href)}>
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="secondary"
          onClick={() => navigate('/delivery-reconciliation')}>
          {t('financeDashboardViewDeliveryReconciliation', 'View Delivery Reconciliation')}
        </Button>
        <Button variant="secondary"
          onClick={() => navigate('/invoices')}>
          {t('financeDashboardViewInvoices', 'View Invoices')}
        </Button>
      </Box>
    </Box>
  );
};

export default OverviewTab;
